import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import { getFrequencyStatisticsByLocalId } from '../../../../../../crud/management_api/sales_statistics';
import { ReceiptFrequencyResponse, DayOfWeek, ViewMode, DayFrequency, DAY_LABELS } from './types';
import FrequencyChart from './components/FrequencyChart/FrequencyChart';
import StatCards from './components/StatCards/StatCards';
import RankingTables from './components/RankingTables/RankingTables';

interface FrequencyStatsProps {
  startDate?: string;
  endDate?: string;
  selectedDay: DayOfWeek;
  viewMode: ViewMode;
  onSelectedDayChange: (day: DayOfWeek) => void;
  onViewModeChange: (mode: ViewMode) => void;
}

const FrequencyStats: React.FC<FrequencyStatsProps> = ({ 
  startDate, 
  endDate, 
  selectedDay, 
  viewMode,
  onSelectedDayChange,
  onViewModeChange 
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReceiptFrequencyResponse | null>(null);

  useEffect(() => {
    loadFrequencyData();
    // eslint-disable-next-line
  }, [startDate, endDate]);

  const loadFrequencyData = async () => {
    setLoading(true);
    setError(null);
    try {
      const raw = localStorage.getItem('user');
      const localId = raw ? JSON.parse(raw).local_id ?? 0 : 0;

      const response = await getFrequencyStatisticsByLocalId(localId, startDate, endDate);
      setData(response.data);

      // Set initial selected day to the one with most receipts
      if (response.data && response.data.frequency) {
        const dayWithMostReceipts = Object.entries(response.data.frequency)
          .reduce((max, [day, dayData]) => {
            const typedDayData = dayData as DayFrequency;
            const typedMaxData = max[1] as DayFrequency;
            
            const dayTotal = Object.values(typedDayData.buckets).reduce((sum, bucket) => sum + bucket.count, 0);
            const maxTotal = Object.values(typedMaxData.buckets).reduce((sum, bucket) => sum + bucket.count, 0);
            return dayTotal > maxTotal ? [day, dayData] : max;
          })[0] as DayOfWeek;
        
        onSelectedDayChange(dayWithMostReceipts);
      }
    } catch (err) {
      console.error('Error loading frequency statistics:', err);
      setError('No se pudieron cargar las estadísticas de frecuencia');
      toast.error('Error cargando estadísticas de frecuencia');
    } finally {
      setLoading(false);
    }
  };

  const selectedDayData = useMemo(() => {
    if (!data || !data.frequency[selectedDay]) return null;
    return data.frequency[selectedDay];
  }, [data, selectedDay]);

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner text="Cargando estadísticas de frecuencia..." transparent />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4">
        <div className="mp-empty-state">
          <div className="mp-empty-state-icon" style={{ color: 'var(--mediopunto-orange)' }}>⚠</div>
          <p className="mp-empty-state-title">Error al cargar datos</p>
          <p style={{ color: 'var(--text-color)', opacity: 0.5, fontSize: '0.875rem' }}>{error}</p>
          <button className="mp-btn-primary mt-3" onClick={loadFrequencyData}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!data || !selectedDayData) {
    return (
      <div className="flex flex-col gap-4">
        <div className="mp-empty-state">
          <p className="mp-empty-state-title">No hay datos de frecuencia disponibles</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <StatCards data={data} />
      <FrequencyChart
        dayData={selectedDayData}
        selectedDay={DAY_LABELS[selectedDay]}
        viewMode={viewMode}
      />
      <RankingTables rankings={data.rankings} />
    </div>
  );
};

export default FrequencyStats;
